import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import classNames from 'classnames';

import LineLoader from 'common/dist/components/LineLoader';
import RegistrationModal from '../../scenes/Login/Registration/Modal';
import RegistrationForm, {
  FormModel,
} from '../../scenes/Login/Registration/RegistrationForm';
import EclipseLoader from '../EclipseLoader';

import * as api from '../../api';
import * as store from '../../reducers/store';
import * as rootReducer from '../../reducers/root';
import * as logger from '../../logger';

import { Option } from '../utils';
import {
  getContactVerificationFields,
  getErrorPageByStatusCode,
  MINIMUM_LOAD_TIME,
  Page,
} from '../../scenes/Login/Registration';
import { parseValue } from 'common/dist/utils/types';

import formStyles from '../../scenes/FormStart/index.module.css';
import introPageStyles from '../../scenes/FormStart/IntroPage/index.module.css';
import styles from '../../scenes/Login/index.module.css';
import registrationStyles from './index.module.css';
import { verifyRequest } from '../../utils';
import SsoButton from '../SsoButton';

interface StateProps {
  theme: api.PracticeTheme.ThemeResponse | null;
  authCode: string | null;
  previsitSexOptions: api.Previsit.PracticePreference['previsitSexOptions'];
  provisionalPatientId: string | null;
  previsitUseNotionalSchedulingTimeSlots: boolean;
}

interface DispatchProps {
  setOnCaptchaSuccess: (onCaptchaSuccess: (() => void) | null) => void;
  setFormField: (formFieldId: string, value: string) => void;
  setIsProvisionalPatientReconciled: (
    isProvisionalPatientReconciled: boolean,
  ) => void;
}

interface Props extends StateProps, DispatchProps {
  navigateToNextPage: () => Promise<void>;
  onRegistrationCompleted: (
    registration: FormModel & {
      authenticationCode: string;
    },
  ) => Promise<void>;
  onError?: () => void;
  submitButtonText: string;
  practiceId?: string;
  formField: api.PrevisitFormField.PrevisitFormFieldWithConfig;
}

const Registration: React.FC<Props> = ({
  theme,
  navigateToNextPage,
  onRegistrationCompleted,
  submitButtonText,
  practiceId,
  authCode,
  formField,
  previsitSexOptions,
  onError,
  provisionalPatientId,
  setOnCaptchaSuccess,
  setFormField,
  setIsProvisionalPatientReconciled,
  previsitUseNotionalSchedulingTimeSlots,
}) => {
  const { t } = useTranslation('login');
  const { schedulingCode } = useParams<{ schedulingCode: string }>();
  const [page, setPage] = useState(Page.Login);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formValues, setFormValues] = useState<FormModel | undefined>();
  const [provisionalPatient, setProvisionalPatient] =
    useState<api.ProvisionalPatient.ProvisionalPatient | null>(null);
  const [verificationOptions, setVerificationOptions] = useState<
    Option<api.Register.AllowedVerification>[]
  >([]);

  const { additionalLoginInputs } = formField
    ? { additionalLoginInputs: formField.registrationAdditionalLoginInputs }
    : { additionalLoginInputs: theme?.additionalLoginInputs };

  const onRegistrationCompletedHandler = useCallback(
    async (
      registration: FormModel & {
        authenticationCode: string;
      },
    ) => {
      setPage(Page.Booking);
      if (
        registration.reconciliationStatus ===
          api.Register.ReconciliationStatus.Success ||
        previsitUseNotionalSchedulingTimeSlots
      ) {
        setIsProvisionalPatientReconciled(true);
      }
      try {
        const value = JSON.stringify(registration);
        setFormField(formField.id, value);
        await api.client.Previsit.updateFormField(
          formField.previsitFormSetId,
          formField.id,
          value,
        );
        await onRegistrationCompleted(registration);
        await navigateToNextPage();
      } catch (e) {
        logger.error(e);
        setPage(Page.Login);
        onError && onError();
      }
    },
    [
      onRegistrationCompleted,
      navigateToNextPage,
      onError,
      formField.id,
      formField.previsitFormSetId,
      setFormField,
      previsitUseNotionalSchedulingTimeSlots,
      setIsProvisionalPatientReconciled,
    ],
  );

  useEffect(() => {
    if (!formField.value) return;
    if (!authCode) return;
    if (page !== Page.Login) return;
    const values = parseValue<FormModel>(formField.value);
    values &&
      onRegistrationCompletedHandler({
        ...values,
        authenticationCode: authCode,
      });
    // Just once on load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (values: FormModel) => {
    const { firstName, lastName, dateOfBirth, cellPhoneNumber, email, sex } =
      values;
    setFormValues(values);
    const { hostname } = window.location;
    const domain = hostname;

    setIsSubmitting(true);
    setPage(Page.Loading);

    const payload = {
      registrationCode: schedulingCode,
      domain,
      firstName,
      lastName,
      dateOfBirth,
      ...{ cellPhoneNumber },
      ...{ email },
      ...(practiceId && {
        practiceId,
      }),
      ...{ sex },
    };
    const start = new Date();

    try {
      await verifyRequest(
        () =>
          api.client.Previsit.register({
            ...payload,
            previsitFormTemplateFieldConfigId:
              formField.previsitFormTemplateFieldConfigId,
            provisionalPatientId: provisionalPatientId || undefined,
          }),
        async (
          registered:
            | {
                authenticationCode: string;
              }
            | api.Register.RegistrationContactVerification,
        ) => {
          'patient' in registered && setProvisionalPatient(registered.patient);

          if ('contactVerification' in registered) {
            const verifications = getContactVerificationFields(
              registered.allowedVerifications,
            );
            setVerificationOptions(verifications);
            setIsModalOpen(true);
          } else if (values && 'authenticationCode' in registered) {
            await onRegistrationCompletedHandler({
              ...values,
              authenticationCode: registered.authenticationCode,
            });
          }
        },
        setOnCaptchaSuccess,
      );
    } catch (err) {
      logger.error(err);
      const registerError = parseValue<{ status: number }>(err.message);
      const page = await getErrorPageByStatusCode(
        start,
        t,
        registerError?.status,
      );
      setPage(page);
      return;
    } finally {
      setIsSubmitting(false);
    }
    const timeElapsed = new Date().getTime() - start.getTime();
    if (timeElapsed <= MINIMUM_LOAD_TIME) {
      await new Promise((res) =>
        setTimeout(res, MINIMUM_LOAD_TIME - timeElapsed),
      );
    }
    setIsSubmitting(false);
    setPage(Page.Success);
  };

  if (page === Page.Loading) {
    return (
      <div>
        <EclipseLoader message={t('Loading...')} />
      </div>
    );
  }

  if (page === Page.Ineligible) {
    return (
      <div className={formStyles.Form}>
        <div className={styles.Flex}>
          <div className={introPageStyles.Label}>
            {t('Something went wrong. Please try again in few minutes.')}
          </div>
        </div>
      </div>
    );
  }

  if (page === Page.Booking) {
    return (
      <div className={classNames(registrationStyles.CenteredContent)}>
        <LineLoader message={t('Retrieving patient information')} />
      </div>
    );
  }

  return (
    <div className={classNames(styles.LoginWrapper, styles.Registration)}>
      <h3 className={styles.PageTitle}>
        {t('Add your information to finish scheduling')}
      </h3>

      <div className={classNames(styles.Login, registrationStyles.FormWrapper)}>
        <SsoButton
          onSsoSuccess={(patient) => {
            onRegistrationCompletedHandler({
              ...patient,
              reconciliationStatus: api.Register.ReconciliationStatus.Success,
            });
          }}
          onSsoFailure={() => {
            onError?.();
          }}
        />
        <RegistrationForm
          formValues={formValues}
          handleSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          submitButtonLabel={t(submitButtonText)}
          additionalLoginInputs={additionalLoginInputs ?? undefined}
          previsitSexOptions={previsitSexOptions}
        />
      </div>
      <RegistrationModal
        isModalOpen={isModalOpen}
        provisionalPatientId={
          (provisionalPatient && provisionalPatient.id) || null
        }
        setIsModalOpen={setIsModalOpen}
        verificationOptions={verificationOptions}
        onPatientVerifiedAndLoggedIn={async (args: {
          authenticationCode: string;
          reconciliationStatus: api.Register.ReconciliationStatus;
        }) => {
          if (!formValues) return;
          await onRegistrationCompletedHandler({
            ...formValues,
            ...args,
          });
        }}
      />
    </div>
  );
};

const mapStateToProps = (store: store.FormSetMaybeSelected): StateProps => ({
  theme: store.theme,
  authCode: store.authenticationCode,
  previsitSexOptions: store.masterPracticePreference.previsitSexOptions,
  provisionalPatientId: store.inbound.provisionalPatientId,
  previsitUseNotionalSchedulingTimeSlots:
    store.masterPracticePreference.previsitUseNotionalSchedulingTimeSlots,
});

function mapDispatchToProps(dispatch: Function): DispatchProps {
  return {
    setOnCaptchaSuccess: (onCaptchaSuccess: (() => void) | null) =>
      dispatch(rootReducer.setOnCaptchaSuccess(onCaptchaSuccess)),
    setFormField: (formFieldId: string, value: string) =>
      dispatch(rootReducer.setFormField({ formFieldId, value })),
    setIsProvisionalPatientReconciled: (
      isProvisionalPatientReconciled: boolean,
    ) =>
      dispatch(
        rootReducer.setIsProvisionalPatientReconciled(
          isProvisionalPatientReconciled,
        ),
      ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Registration);
